import { Component, OnInit } from '@angular/core';
import { MessageBoxComponent } from '@common/popups/message-box/message-box.component';
import { LayoutService } from '@common/services/layout.service';
import { ModalService } from '@common/services/modal.service';
import { NumberInputComponent } from '@common/UI/number-input/number-input.component';
import { SortableExtractor } from '@common/UI/sortable/sortable.component';
import { Filterable } from '@common/utilities/Filterable';
import { Lookup } from '@common/utilities/Lookup';
import { commit, main } from '@core/operators';
import { ApiService } from '@core/services/api.service';
import { CurrenciesService } from '@core/services/currencies.service';
import { calculateExportPrice, DataService, OrderDetailRecord, OrderRecord, OrderState, orderState, PartnerRecord, PolicyRecord, ProductColorRecord, ProductRecord, UserRecord } from '@core/services/data.service';
import { PartnersService } from '@core/services/partners.service';
import { ProductsService } from '@core/services/products.service';
import { SynchronizationService } from '@core/services/synchronization.service';
import { UsersService } from '@core/services/users.service';
import { Multilingual } from '@core/utilities/multilingual';
import { map } from 'rxjs/operators';
import { OrderDetail } from '@routes/invoices/imports/imports.component';

interface SKUColor {
  SKU: string;
  Color: string;
  Product: ProductRecord;
}
  

interface Lookups {
  policies: Lookup<PolicyRecord>;
  partners: PartnersService;  
  currencies: CurrenciesService;
  users: UsersService;

  products: Lookup<SKUColor>;
  colors: { [key: string]: ProductColorRecord };
}



const lu: Lookups = {
  policies: new Lookup<PolicyRecord>(i => i.ProductID),
  partners: null,
  products: new Lookup<SKUColor>(i => [i.SKU, i.Color].join('/')),
  currencies: null,
  users: null,
  colors: {}
}

@Component({
  selector: 'app-exports',
  templateUrl: './exports.component.html',
  styleUrls: ['./exports.component.scss']
})
export class ExportsComponent implements OnInit {

  constructor(
    private layout: LayoutService,
    private ds: DataService,
    private api: ApiService,
    private modal: ModalService,
    private sync: SynchronizationService,
    private _currencies: CurrenciesService,
    private _partners: PartnersService,
    private _products: ProductsService,
    private _users: UsersService
  ) {

    this.sync.when(CurrenciesService, PartnersService, ProductsService, UsersService).commit(() => {
      lu.currencies = this._currencies;
      lu.partners = this._partners;
      lu.products.source = this._products.all.many(i => i.Colors.map(c => ({ SKU: i.SKU, Color: c.Color, Product: i })));
      lu.users = this._users;
      lu.products.source.map(i => i.Product).distinct().commit(product => product.Colors.forEach(color => lu.colors[product.SKU + "/" + color.Color] = color)); 
    });
  }

  public get loading(): boolean {
    return !this.sync.ready(ApiService, CurrenciesService, PartnersService, ProductsService, UsersService);
  }

  public orders = new Filterable<OrderRecord>({ grouper: i => [{ name: 'Khách hàng', option: '#' + i.PartnerID }] });

  public partners = new Filterable<PartnerRecord>({ extractor: i => [i.Name] });
  public products = new Filterable<ProductRecord>({ extractor: i => [i.SKU] });

  public selected: { order: OrderRecord, partner: PartnerRecord } = { order: null, partner: null };

  private edited: boolean = false;

  public synchronize() {

    let orders: OrderRecord[] = [];
    this.sync.with(ExportsComponent).repeat(() => this.ds.orders("incompleted").pipe(map(data => data ? commit<boolean>(() => orders = data).return(true) : false)));

    this.sync.when(ApiService, CurrenciesService, UsersService, PartnersService, ProductsService, ExportsComponent).commit(() => {

      this.orders.source = orders.filter(i => !i.Notified || i.State == OrderState.Confirmed);

      this.partners.source = lu.partners.all.filter(i => this.isOwner || i.DealerID == this._users.current.ID);      
    });
  }

  ngOnInit(): void {

    main(() => {
      this.layout.media('footer', 'none');
      this.layout.title = Multilingual.getText("Khách đặt hàng");
    });

    this.synchronize();
  }

  public get isAdmin(): boolean {
    return lu.users.current && (lu.users.current.Roles.contains("Admin") || lu.users.current.Roles.contains("Owner"));
  }

  public get isOwner(): boolean {
    return lu.users.current && lu.users.current.Roles.contains("Owner");
  }

  public get editable(): boolean {
    return this.selected.order && this.selected.order.State == OrderState.Editable;
  }

  public get savable(): boolean {
    return this.selected.order != null && this.selected.order.Details.length > 0 && this.edited;
  }

  public get confirmable(): boolean {

    return this.selected.order != null
      && this.selected.order.State == OrderState.Editable
      && this.selected.order.Details.length > 0
      && this.selected.order.Details.count(i =>
        i.QuoteUnitPrice <= 0
        || i.ImportUnitPrice <= 0
        ) < 1
      && !this.edited;
  }

  public get unconfirmable(): boolean {
    return this.selected.order && this.selected.order.State == OrderState.Confirmed && !this.selected.order.Notified;
  }

  public get cancelable(): boolean {
    return this.selected.order != null && this.selected.order.State == OrderState.Editable && ((this.selected.order.Details.length > 0 && !this.edited) || this.selected.order.ID == 0 || (this.selected.order.Details.length < 1 && this.edited));
  }

  public user(id: number): UserRecord {
    return lu.users.get(id);
  }

  public partner(order: OrderRecord): PartnerRecord {
    return lu.partners.get(order.PartnerID);
  }

  public id(order: OrderRecord): string {
    return order.Tag && order.Tag.length > 0 ? order.Tag : '#' + order.ID;
  }

  public state = orderState;

  public price(product: ProductRecord, ignoreAdjustment: boolean = false): number {
    return calculateExportPrice(product, lu.currencies.get('CNY'), lu.currencies.get('VND'), ignoreAdjustment ? 0 : lu.policies.get(product.ID).Adjustment);
  }

  public count(order: OrderRecord): number {
    return order.Details.length;
  }

  public quantity(order: OrderRecord): number {
    return order.Details.sum(i => i.Quantity);
  }

  public completion(order: OrderRecord): number {
    return order.Details.sum(i => Math.min(i.Provided, i.Quantity)) / order.Details.sum(i => i.Quantity);
  }

  public adjustment(detail: OrderDetailRecord): number {
    let id = lu.products.get([detail.SKU, detail.Color].join('/')).Product.ID;
    return -lu.policies.has(id) ? lu.policies.get(id).Adjustment : 0;
  }

  public setAdjustment(detail: OrderDetailRecord, input: NumberInputComponent | number) {

    this.ds
      .policies("update", {
        ProductID: this._products.get(detail.SKU, detail.Color)[0].ID,
        Adjustment: -(typeof input == 'number' ? input as number : (input as NumberInputComponent).value),
        Icon: null,
        Symbol: 'CNY',
        CustomerID: this.selected.order.PartnerID
      })
      .subscribe(results => {
        if (results && results.length == 1) {

          lu.policies.get(results[0].ProductID).Adjustment = results[0].Adjustment;
          this.selected.order.Details.filter(i => i.SKU == detail.SKU && i.Color == detail.Color).commit(i => i.QuoteUnitPrice = this.price(lu.products.get([i.SKU, i.Color].join('/')).Product));
          this.edited = true;
        }
      })
  }

  public setPrice(detail: OrderDetailRecord, input: NumberInputComponent) {

    if (isNaN(input.value)) input.value = this.price(lu.products.get([detail.SKU, detail.Color].join('/')).Product)
    this.setAdjustment(detail, Math.max(0, this.price(lu.products.get([detail.SKU, detail.Color].join('/')).Product, true) - input.value) / lu.currencies.get('CNY').Conversion);
  }

  public extractor(field: string): SortableExtractor<OrderRecord> {
    return {
      extract: i => {
        switch (field) {
          case 'Partner': return this.partner(i).Name;
          case 'Quantity': return this.quantity(i);
          case 'Count': return this.count(i);
          case 'Creator': return this.user(i.CreatorID).Username;
          case 'Completion': return this.completion(i);
          default: return i[field];
        }
      }
    }
  }

  public toggle(partner: PartnerRecord) {
    this.selected.partner = partner == this.selected.partner ? null : partner;

    if (this.selected.partner) {
      this.orders.groups.forEach(i => i.selected = i.option == '#' + this.selected.partner.ID);
    }
    else {
      this.orders.groups.forEach(i => i.selected = true);
    }
  }

  public open(order: OrderRecord) {

    let policies: PolicyRecord[] = [];

    if (order.State == OrderState.Editable) {
      this.sync.with(ExportsComponent).repeat(
        () => this.ds.policies('get', { CustomerID: order.PartnerID }).pipe(map(data => data ? commit<boolean>(() => policies = data).return(true) : false))
      );
    }

    this.sync.when(ExportsComponent, ProductsService, PartnersService).commit(() => {

      lu.policies.source = policies;

      if (order.State == OrderState.Editable) {
        order = {
          ...order,
          Details: order.Details.map(i => {

            if (i.QuoteUnitPrice != this.price(lu.products.get([i.SKU, i.Color].join('/')).Product)) {
              this.edited = true;
              return { ...i, QuoteUnitPrice: this.price(lu.products.get([i.SKU, i.Color].join('/')).Product), ImportUnitPrice: lu.products.get([i.SKU, i.Color].join('/')).Product.UnitPrice };
            }

            return { ...i };
          })
        };
      }

      this.selected.partner = this.partner(order);
      this.selected.order = order;

      order.Details.forEach(i => {
        let product = lu.products.get([i.SKU, i.Color].join('/')).Product;
        product.Colors = product.Colors.filter(x => x.Color != i.Color);
      });

      this.products.source = lu.products.source.map(i => i.Product).distinct().filter(i => i.Colors.length > 0);

      this.orders.groups.forEach(i => i.selected = i.option == '#' + this.selected.partner.ID);
    })
  }
  
  public importPrice(detail: OrderDetailRecord): number {
    return lu.products.get([detail.SKU, detail.Color].join('/')).Product.UnitPrice;
  }

  public close() {

    if (this.edited) {

      this.modal.show(MessageBoxComponent, {
        ignoreBackdropClick: true, state: {
          closable: false,
          message: "Đơn đặt hàng cho " + this.selected.partner.Name + " có các thay đổi chưa lưu lại. Đóng đơn sẽ không lưu lại các thay đổi đó. Bạn có muốn đóng đơn?",
          title: "Xác nhận",
          buttons: [
            {
              icon: 'fa fa-check', class: 'bg-warning text-white', name: 'Đóng đơn', click: e => {

                this.selected.order = null;
                this.selected.partner = null;
                this.orders.groups.forEach(i => i.selected = true);
                this.edited = false;
              }
            },

            { icon: "fa fa-undo", class: 'bg-success text-white', name: 'Quay lại' }
          ]
        }
      });
    }
    else {
      this.selected.order = null;
      this.selected.partner = null;
      this.orders.groups.forEach(i => i.selected = true);
      this.edited = false;
    }
  }

  public create() {

    this.open({
      ID: 0,
      CreatorID: this.api.user.ID,
      UpdatorID: this.api.user.ID,
      QuoteCurrency: "VND",
      ImportCurrency: "CNY",
      State: OrderState.Editable,
      FirstStamp: new Date().getTime(),
      LastStamp: new Date().getTime(),
      Stamp: new Date().getTime(),
      Details: [],
      PartnerID: this.selected.partner.ID,
      Tag: '',
      Completed: false,
      Notified: false
    });
  }

  public add(product: ProductRecord, color: string) {

    let detail: OrderDetailRecord = {
      ID: 0,
      OrderID: 0,
      SKU: product.SKU,
      Color: color,
      Unit: product.Unit,
      Quantity: 1,
      ExpectedDelay: 0,
      ExpectedUnitQuantity: Math.floor(product.AverageQuantity),
      Provided: 0,
      Verified: true,
      QuoteUnitPrice: this.price(product),
      ImportUnitPrice: product.UnitPrice,
      QuoteConversion: lu.currencies.get("CNY").Conversion
    };

    this.selected.order.Details.push(detail);

    product.Colors = product.Colors.filter(i => i.Color != color);
    if (product.Colors.length == 0) this.products.source = this.products.source.filter(i => i != product);

    this.edited = true;
  }

  public remove(detail: OrderDetailRecord) {

    this.selected.order.Details = this.selected.order.Details.filter(i => i != detail);
    lu.products.get([detail.SKU, detail.Color].join('/')).Product.Colors.push(lu.colors[detail.SKU + "/" + detail.Color]);

    if (lu.products.get([detail.SKU, detail.Color].join('/')).Product.Colors.length == 1) this.products.source = this.products.source.union([lu.products.get([detail.SKU, detail.Color].join('/')).Product]);

    this.edited = true;
  }

  public deadline(detail: OrderDetailRecord): Date {
    return new Date(this.selected.order.Stamp).addDays(detail.ExpectedDelay);
  }

  public set(detail: OrderDetailRecord, input: NumberInputComponent, field: string, removable) {

    if (isNaN(input.value)) input.value = 0;
    if (input.value == 0 && removable) {
      this.remove(detail);
    }
    else {
      detail[field] = input.value;

      let product = this._products.get(detail.SKU, detail.Color).first();

      switch (field) {
        case 'Quantity':
          if (product.AverageQuantity > 0) {
            detail.ExpectedUnitQuantity = Math.floor(detail.Quantity * this._products.get(detail.SKU, detail.Color).first().AverageQuantity);
          }
          break;
      }

    }

    this.edited = true;
  }

  public average(detail: OrderDetailRecord): number {
    let product = this._products.get(detail.SKU, detail.Color).first();
    return product ? product.AverageQuantity : 0;
  }

  public save() {

    this.ds.orders(this.selected.order.ID > 0 ? 'update' : 'create', this.selected.order).subscribe(order => {

      if (order && order.length == 1) {

        this.orders.source = this.orders.source.filter(i => i.ID != this.selected.order.ID).union([{ ...order[0], Details: order[0].Details.map(i => ({ ...i })) }]);
        this.selected.order = order[0];

        this.edited = false;        
      };
    });
  }

  public cancel() {

    if (this.selected.order.ID == 0 && this.selected.order.Details.length == 0) {
      this.edited = false;
      this.close();
      return;
    }

    this.modal.show(MessageBoxComponent, {
      ignoreBackdropClick: true, state: {
        closable: false,
        message: "Bạn muốn hủy đơn đặt hàng cho " + this.selected.partner.Name + "?",
        title: "Xác nhận",
        buttons: [
          {
            icon: 'fa fa-times', class: 'bg-danger text-white', name: 'Hủy đơn', click: e => {

              if (this.selected.order.ID == 0) {
                this.edited = false;
                this.close();
              }
              else {

                this.ds.orders('cancel', this.selected.order).subscribe(order => {
                  if (order) {
                    this.orders.source = this.orders.source.filter(i => i.ID != this.selected.order.ID);
                    this.edited = false;
                    this.close();
                  }
                });
              }
            }
          },

          { icon: "fa fa-undo", class: 'bg-success text-white', name: 'Quay lại' }
        ]
      }
    });
  }

  public unconfirm() {

    this.modal.show(MessageBoxComponent, {
      ignoreBackdropClick: true, state: {
        closable: false,
        message: "Bạn muốn mở lại đơn đặt hàng cho " + this.selected.partner.Name + "?",
        title: "Xác nhận",
        buttons: [
          {
            icon: 'fa fa-times', class: 'bg-warning text-white', name: 'Mở lại đơn', click: e => {

              this.ds.orders('unconfirm', this.selected.order).subscribe(order => {
                if (order && order.length == 1) {

                  this.orders.source = this.orders.source.filter(i => i.ID != this.selected.order.ID).union(order);
                  this.edited = false;
                  this.open(order[0]);

                }
              });
            }
          },

          { icon: "fa fa-undo", class: 'bg-success text-white', name: 'Quay lại' }
        ]
      }
    });
  }

  public confirm() {
    this.modal.show(MessageBoxComponent, {
      ignoreBackdropClick: true, state: {
        closable: false,
        message: "Bạn muốn xác nhận đơn đặt hàng cho " + this.selected.partner.Name + "?",
        title: "Xác nhận",
        buttons: [
          {
            icon: 'fa fa-check', class: 'bg-warning text-white', name: 'Xác nhận đơn', click: e => {

              this.ds.orders('confirm', this.selected.order).subscribe(order => {
                if (order && order.length == 1) {

                  this.orders.source = this.orders.source.filter(i => i.ID != this.selected.order.ID).union([{ ...order[0], Details: order[0].Details.map(i => ({ ...i })) }]);
                  this.close();
                }
              });
            }
          },

          { icon: "fa fa-undo", class: 'bg-success text-white', name: 'Quay lại' }
        ]
      }
    });
  }
}
